import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slide,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { createDiscount, updateDiscount } from "../../../Services/authService";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import { StyledTextField } from "../../../Components/Common/StyledTextField";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LocationPopup from "../../Views/LocationPopup";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import { CalendarToday } from "@mui/icons-material";

// Slide Transition Function
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} timeout={800} />;
});

function DiscountModal({ open, onClose, selectedCard, fetchDiscounts }) {
  const { t, i18n } = useTranslation();
  const { mode } = useThemeContext();

  const btnColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const modalBackgrounColor =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const inputBoxColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const subTextColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;
  const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;

  const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocationsDetails, setSelectedLocationsDetails] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedALLLocations, setSelectedAllLocations] = useState(false);

  const [formData, setFormData] = useState({
    // id: 0,
    discountPercentage: "",
    name: "",
    startDate: "",
    endDate: "",
    vendingMachines: [],
  });

  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successNotification, setSuccessNotification] = useState(false);
  // Capture edit vs. create at save time: the parent nulls `selectedCard` on
  // close, so the notification can't rely on it at render time.
  const [isEditSuccess, setIsEditSuccess] = useState(false);

  useEffect(() => {
    setSelectedLocationsDetails([]);
    setSelectedLocations([]);
    if (selectedCard) {
      setFormData(selectedCard);
      setSelectedLocations(selectedCard.vendingMachines); // Combine vendingMachineIds and vendingMachineDetails into a single object
      const combinedLocations = selectedCard.vendingMachines.map(
        (machineId, index) => ({
          machineId,
          ...selectedCard.vendingMachineDetails[index], // Assuming vendingMachineDetails is an array with corresponding details
        }),
      );
      setSelectedLocationsDetails(combinedLocations);
      // Update formData with vendingMachineIds while preserving other fields
      setFormData((prevFormData) => ({
        ...prevFormData, // Preserve existing form data fields
        vendingMachines: selectedCard.vendingMachines, // Add or update vendingMachineIds
      }));
      setSelectedLocations(selectedCard.vendingMachines);
    } else {
      setFormData({
        // id: 0,
        name: "",
        discountPercentage: "",
        startDate: "",
        endDate: "",
        vendingMachines: [],
      });
    }
  }, [selectedCard]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Percentage allows at most 2 decimal places while typing (15.25 ok, 15.255 rejected)
    if (
      name === "discountPercentage" &&
      value &&
      !/^\d{0,3}(\.\d{0,2})?$/.test(value)
    ) {
      return;
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    // Required-empty fields set a `true` flag (red highlight only); invalid
    // values set the specific message string, which wins over the generic one
    if (!formData.name?.trim()) {
      newErrors.name = true;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("coupons.nameMinLength");
    }
    if (!formData.discountPercentage) {
      newErrors.discountPercentage = true;
    } else if (!/^\d+(\.\d{1,2})?$/.test(String(formData.discountPercentage))) {
      // Catches >2 decimals that slip in via paste despite the typing guard
      newErrors.discountPercentage = t("discounts.maxTwoDecimals");
    } else if (
      formData.discountPercentage < 1 ||
      formData.discountPercentage > 100
    ) {
      newErrors.discountPercentage = t("discounts.DISCOUNT_RANGE");
    }

    if (!formData.startDate) newErrors.startDate = true;
    if (!formData.endDate) newErrors.endDate = true;
    if (formData.vendingMachines.length === 0) {
      newErrors.vendingMachines = true;
    }
    setErrors(newErrors);
    // Show one message only: a specific error (name too short, percentage out
    // of range) wins over the generic "please fill" fallback
    const specificError = Object.values(newErrors).find(
      (v) => typeof v === "string",
    );
    setGeneralError(
      Object.keys(newErrors).length === 0
        ? ""
        : specificError || t("membershipCards.Pleasefill"),
    );

    return Object.keys(newErrors)?.length === 0;
  };

  // Re-run validation on language switch so visible messages re-translate
  // (they are stored in state as already-translated strings)
  useEffect(() => {
    if (Object.keys(errors).length > 0) validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handleSave = async () => {
    setGeneralError("");
    setErrors({});
    if (!validateForm()) return;
    try {
      setLoading(true);
      // Convert discountPercentage to a number
      const updatedFormData = {
        ...formData,
        discountPercentage: Number(formData.discountPercentage),
        startDate: formData.startDate, // Format startDate to ISO string
        endDate: formData.endDate, // Format endDate to ISO string
      };
      if (updatedFormData.id) {
        await updateDiscount(updatedFormData.id, updatedFormData);
        setIsEditSuccess(true);
      } else {
        await createDiscount(updatedFormData);
        setIsEditSuccess(false);
      }

      // Reset the form
      setFormData({
        // id: 0,
        name: "",
        discountPercentage: "",
        startDate: "",
        endDate: "",
        vendingMachines: [],
      });

      setSelectedLocations([]); // Clear selected locations
      setSuccessNotification(true); // Show success notification
      setSelectedLocationsDetails([]);
      onClose(); // Close the dialog
      fetchDiscounts(); // Refresh the table data
    } catch (error) {
      console.error(t("discounts.errorSaving"), error);

      const detail = error?.response?.data?.detail;

      if (error.response?.status === 409) {
        // 409 Conflict — could be name conflict or machine/date overlap, distinguish by detail
        if (detail?.includes("name already exists")) {
          setGeneralError(t("discounts.discountNameAlreadyExists"));
        } else {
          setGeneralError(t("discounts.CONFLICT"));
        }
      } else if (detail?.includes("already exists")) {
        setGeneralError(t("discounts.discountNameAlreadyExists"));
      } else {
        setGeneralError(
          detail || t("membershipCards.somethingWentWrong")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSubmit = (locations, isAllSelected) => {
    setSelectedAllLocations(isAllSelected);
    // Variables to store selected machines and machine details separately
    const selectedMachineIds = [];
    const selectedMachinesDetails = [];

    // Loop through locations to gather selected machine data
    locations.forEach((location) => {
      // For each machine in the location, store its full details
      location.machines.forEach((machine) => {
        // Push the machine details with location info into selectedMachinesDetails
        selectedMachinesDetails.push({
          machineId: machine.machineId,
          street: machine.street,
          city: location.location,
          location: machine.location, // Add location field as well
        });

        // Add machine ID to selectedMachineIds
        selectedMachineIds.push(machine.machineId);
      });
    });
    // Update the formData with selected machine IDs
    setSelectedLocationsDetails(selectedMachinesDetails); // Store full details
    setFormData({
      ...formData,
      vendingMachines: selectedMachineIds, // Update formData with only machine IDs
    });
    setErrors((prev) => ({
      ...prev,
      vendingMachines: "",
    }));
  };
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  const resetForm = () => {
    setFormData({
      name: "",
      discountPercentage: "",
      startDate: "",
      endDate: "",
      vendingMachines: [],
    });

    setSelectedLocations([]);
    setSelectedLocationsDetails([]);
    setSelectedAllLocations(false);

    setErrors({});
    setGeneralError("");
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);
  return (
    <>
      {loading && <LoadingAnimation />}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            position: "absolute",
            right: 10,
            margin: 0,
            // height: '100vh', // Full height
            borderRadius: 3,
            background: modalBackgrounColor,
            width: "97%",
            "@media (max-width: 427px)": {
              right: 5,
            },
          },
        }}
      >
        {/* Title with Close Icon */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
          }}
        >
          {" "}
          <span className="franchise-heading-text">
            {selectedCard
              ? t("discounts.editDiscount")
              : t("discounts.createDiscount")}
          </span>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            paddingTop: 2,
            p: {
              xs: 2, // base padding
              sm: 3, // small screens
              md: "20px 24px ",
            },
             marginRight: 1,
            '&::-webkit-scrollbar': {
              width: '8px !important',
              height: '8px !important',
              padding: '2px !important',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: scroll1,
              borderRadius: '10px',
              border: `1px solid ${scroll}`,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: scroll,
              borderRadius: '10px',
              width: '3px !important',
              border: '3px solid transparent',
              backgroundClip: 'padding-box',
              '&:hover': {
                backgroundColor: scroll,
              },
            },
              // const scroll1 = mode === 'light' ? LIGHTCOLORS.BodyDarkBackground : DARKCOLORS.BodyLightBackground;
            
              // const scroll = mode === 'light' ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;
            
          }}
        >
          {" "}
          <Box
            component="form"
            noValidate
            autoComplete="off"
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Name */}
            <Box>
              <StyledTextField
                label={`${t("discounts.nameOfDiscount")}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                inputBoxColor={inputBoxColor}
                required
              />
            </Box>

            {/* percentage */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <StyledTextField
                numberType="number"
                label={`${t("discounts.discountPercentage")}`}
                name="discountPercentage"
                placeholder="%"
                value={formData.discountPercentage}
                onChange={handleChange}
                error={errors.discountPercentage}
                inputBoxColor={inputBoxColor}
                required
              />
              
            </Box>

            {/* Start Date */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                display: "flex",
                alignItems: "center",
                backgroundColor: inputBoxColor,
                borderRadius: "8px",
              }}
            >
              <StyledTextField
                label={`${t("discounts.Fromdate")}`}
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                error={errors.startDate}
                required
                isDate // Pass isDate to handle date-specific styling and behavior
                inputBoxColor={inputBoxColor}
                inputProps={{
                  min: today, // Disable past dates
                }}
              />
              <CalendarToday
                sx={{
                  fontSize: "14px",
                  position: "absolute",
                  top: 29,
                  right: 15,
                  opacity: mode === "dark" ? 100 : 0, // Change color based on theme
                  color: mode === "dark" ? "#fff" : "#000", // Change color based on theme
                  pointerEvents: "none",
                }}
              />
              {/* </Box> */}
            </Box>

            {/* End Date */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                display: "flex",
                alignItems: "center",
                backgroundColor: inputBoxColor,
                borderRadius: "8px",
              }}
            >
              <StyledTextField
                label={`${t("discounts.Todate")}`}
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                error={errors.endDate}
                required
                isDate // Pass isDate to handle date-specific styling and behavior
                inputBoxColor={inputBoxColor}
                inputProps={{
                  min: formData.startDate || today, // Ensure the end date is after or equal to the start date
                }}
              />
              <CalendarToday
                sx={{
                  fontSize: "14px",
                  position: "absolute",
                  top: 29,
                  right: 15,
                  opacity: mode === "dark" ? 100 : 0, // Change color based on theme
                  color: mode === "dark" ? "#fff" : "#000", // Change color based on theme
                  pointerEvents: "none",
                }}
              />
              {/* </Box> */}
            </Box>

            {/* Select Locations */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                borderRadius: "8px",
                width: "100%",
                backgroundColor: inputBoxColor,
                cursor: "pointer",
              }}
              onClick={() => setLocationModalOpen(true)}
            >
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {/* Label */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: errors.vendingMachines ? "#d32f2f" : subTextColor,
                    mb: 0.5,
                  }}
                >
                  {t("coupons.selectLocations")}
                </Typography>

                {/* Selected value */}
                <Typography sx={{}}>
                  {selectedLocationsDetails.length > 0
                    ? selectedALLLocations
                      ? t("coupons.allMachinesSelected")
                      : selectedLocationsDetails
                          .map(
                            (location) =>
                              `${location.location} - ${location.street}`,
                          )
                          .join(", ")
                    : ""}
                </Typography>
              </Box>

              <ArrowDropDownIcon sx={{ color: subTextColor }} />
            </Box>

            <LocationPopup
              open={locationModalOpen}
              onClose={() => setLocationModalOpen(false)}
              selectedMachineLocations={selectedLocations}
              onSubmit={handleLocationSubmit}
              excludeMachineType="Return Vending Machine"
            />

            {generalError && (
              <Typography
                color="error"
                variant="body2"
                sx={{ textAlign: "center", marginTop: 1 }}
              >
                {generalError}
              </Typography>
            )}
            {/* Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <button
                type="button"
                style={{ color: btnColor, borderColor: btnColor }}
                className="cancel-btn"
                onClick={onClose}
              >
                {t("membershipCards.cancel")}
              </button>
              <button
                type="button"
                style={{ background: btnColor }}
                className="save-btn"
                onClick={handleSave}
              >
                {t("membershipCards.save")}
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Use Notification Component */}
      <SuccessNotification
        open={successNotification}
        message={t(
          isEditSuccess
            ? "discounts.updateSuccess"
            : "discounts.DiscountSuccessMessage",
        )}
        severity="success"
        onClose={() => setSuccessNotification(false)}
      />
    </>
  );
}

export default DiscountModal;
